import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

export type GetSelectRowPropsType = {
  selected: boolean;
};

export type GetSelectPropsType = {
  id: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  checked: boolean;
};

export type GetSelectAllPropsType = {
  onChange: () => void;
  checked: boolean;
};

export type UseSelectableConfigType = {
  key?: string;
  initialState?: string[];
};

export type UseSelectableStateType = {
  rows: Record<string, boolean>;
  allRows: boolean;
};

type Data = Array<Record<string, any>>;

export type UseSelectableActionsType = {
  setSelectedRows: Dispatch<SetStateAction<Record<string, boolean>>>;
  toggleRow: (event: ChangeEvent<HTMLInputElement>) => void;
  toggleAllRows: () => void;
  getSelectRowProps: (data: Record<string, any>) => GetSelectRowPropsType;
  getSelectProps: (data: Record<string, any>) => GetSelectPropsType;
  getSelectAllProps: () => GetSelectAllPropsType;
  getSelectedKeys: () => unknown[];
  getSelectedItems: () => Array<Record<string, boolean>>;
};

export type UseSelectableType = [UseSelectableStateType, UseSelectableActionsType];

const defaultInitialState = [];

function useSelectable(data: Data = [], config: UseSelectableConfigType = {}): UseSelectableType {
  const { key = 'id', initialState = defaultInitialState } = config;

  const getInitialSelectedState = React.useCallback(
    (data: Data, state = []) =>
      data.reduce((acc, current) => {
        const objectIsInInitialState = initialState.includes(current[key]);
        const objectIsInPreviousState = !!state[current[key]];
        acc[current[key]] = objectIsInInitialState || objectIsInPreviousState;
        return acc;
      }, {}),
    [key, initialState],
  );
  const [selectedRows, setSelectedRows] = React.useState<Record<string, boolean>>(getInitialSelectedState(data));

  const dataRef = React.useRef(data);

  React.useEffect(() => {
    if (!isEqual(dataRef.current, data)) {
      setSelectedRows(getInitialSelectedState(data, selectedRows));
      dataRef.current = data;
    }
  }, [data, selectedRows, getInitialSelectedState]);

  const areAllRowsSelected: boolean = isEmpty(selectedRows) ? false : Object.values(selectedRows).every(v => v);

  function toggleRow(e: ChangeEvent<HTMLInputElement>): void {
    setSelectedRows({ ...selectedRows, [e.target.id]: e.target.checked });
  }

  function toggleAllRows(): void {
    setSelectedRows(
      Object.keys(selectedRows).reduce((acc, key) => {
        acc[key] = !areAllRowsSelected;
        return acc;
      }, {}),
    );
  }

  function getSelectRowProps(d: Record<string, any>): GetSelectRowPropsType {
    return {
      selected: selectedRows[d[key]],
    };
  }

  function getSelectProps(d: Record<string, any>): GetSelectPropsType {
    return {
      id: d[key],
      onChange: toggleRow,
      checked: selectedRows[d[key]],
    };
  }

  const getSelectAllProps = (): GetSelectAllPropsType => ({
    onChange: toggleAllRows,
    checked: areAllRowsSelected,
  });

  const getSelectedKeys = React.useCallback(() => {
    return Object.entries(selectedRows)
      .map(([k, v]) => v && k)
      .filter(a => a);
  }, [selectedRows]);

  const getSelectedItems = React.useCallback(
    () => data.filter(d => getSelectedKeys().includes(d[key])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRows],
  );

  const selected = {
    rows: selectedRows,
    allRows: areAllRowsSelected,
  };

  const actions = {
    setSelectedRows,
    toggleRow,
    toggleAllRows,
    getSelectRowProps,
    getSelectProps,
    getSelectAllProps,
    getSelectedKeys,
    getSelectedItems,
  };

  return [selected, actions];
}

export default useSelectable;
